import React from 'react'
import { toast } from 'react-toastify'
import swal from 'sweetalert2'

import useReduxAction from '@hooks/useReduxAction'
import useClientsV4 from '@hooks/useClientsV4'

import {
  Box, Columns, DashboardModule, ModalContext, PageHeader, LoadingModule,
} from '@campaignhub/suit-theme'

import { useModals, useSetState } from '@campaignhub/react-hooks'
import PageContext from '@contexts/pageContext'
import EditClientModal from '@modals/EditClientModal'

import blankState from '@components/assets/blankState.svg'
import custom from '@styles/custom.module.scss'
import Client from './components/Client'
import Tools from './components/Tools'

const defaultState = {
  defaultCount: 50,
  importDone: true,
  loadNext: false,
  page: 0,
  searchFilter: '',
  selectedClassification: [],
  showEditClientModal: false,
}

const deleteClient = (deleteFn, setState) => {
  deleteFn().then(({ success, errors }) => {
    if (!success && errors){
      toast.warn(errors[0])
      return
    }

    setState({ showEditClientModal: false })
  })
}

const updateClient = (clientParams, updateFn, setState) => {
  updateFn(clientParams).then(({ success, errors }) => {
    if (!success && errors){
      toast.warning(errors[0])
      return
    }

    setState({ showEditClientModal: false })
  })
}

const callbacks = (component, setState) => {
  const componentCallbacks = {
    EditClientModal: {
      closeModal: () => setState({ showEditClientModal: false }),
      deleteClient: deleteFn => deleteClient(deleteFn, setState),
      updateClient: (clientParams, updateFn) => updateClient(clientParams, updateFn, setState),
    },
  }

  return componentCallbacks[component] || {}
}

const importClients = (importFn, setState) => {
  setState({ importDone: false })
  importFn().then(({ success, request, errors }) => {
    if (!success && errors){
      toast.warning(errors[0])
      return
    }
    if (request.data){
      swal.fire({
        title: 'Import Client Details',
        html: `<p><b>Remaining Imports:</b> ${request.data.remainingImports}</p>`
              + `<p><b>Successful Imports:</b> ${request.data.successfulImports}</p>`
              + `<p><b>Failed Imports:</b> ${request.data.failedImports}</p>`,
        icon: 'info',
        confirmButtonText: 'Okay',
        confirmButtonColor: '#e2001a',
      })
      setState({ importDone: true })
    }
  })
}

const ClientList = () => {
  const [state, setState] = useSetState(defaultState)
  const { searchFilter, showEditClientModal, defaultCount,
    importDone, selectedClassification, page, loadNext } = state

  const modalContext = useModals()
  const {
    callbacks: { setModalData },
  } = modalContext

  const updateSearchFilter = (val) => {
    setState({ searchFilter: val, page: 0, loadNext: false })
  }

  const updateSelectedClassification = (checked, val) => {
    if (checked){
      setState({ selectedClassification: [...selectedClassification, val], page: 0, loadNext: false })
    } else {
      setState({ selectedClassification: selectedClassification.filter(i => i !== val), page: 0, loadNext: false })
    }
  }

  const resetFilter = () => {
    setState({ searchFilter: '', selectedClassification: [], page: 0, loadNext: false })
  }

  const { filteredClientsV4, loading, hasMore, callbacks: { importClients: importFn } } = useClientsV4()

  const options = {
    size: defaultCount,
    sort: 'name asc',
    offset: page * defaultCount,
    loadNext,
    name: searchFilter,
    ...selectedClassification.length > 0 && { classification: selectedClassification.join(',') },
  }

  useReduxAction('clientsV4', 'loadClientsV4', options, [searchFilter, selectedClassification, page])

  const loadMore = () => {
    setState({ page: page + 1, loadNext: true })
  }

  const pageContext = {
    callbacks: {
      showEditClientModal: (payload) => {
        setModalData('EditClientModal', payload)
        setState({ showEditClientModal: true })
      },
      searchClientFilter: val => updateSearchFilter(val),
      importClients: () => importClients(importFn, setState),
      toggleSelectedClassification: (checked, val) => updateSelectedClassification(checked, val),
      toggleResetFilter: () => resetFilter(),
    },
    importDone,
    selectedClassification,
    searchFilter,
  }

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageHeader
          activeTabBarItemKey="clients"
          boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
          title="Client Management"
          offset={{ left: 70 }}
        />
        <Box paddingX="large" paddingTop={[112, 105]}>
          <Columns
            boxProps={{ marginTop: 'large' }}
            flexDirection={['column', 'column', 'row']}
          >
            <Columns.Main>
              <DashboardModule title="Clients">
                <Box flexDirection="column" className={custom.scroll}>
                  <Box flexDirection="column" maxHeight="75vh" overflowY="auto">
                    {filteredClientsV4.length > 0 && filteredClientsV4.map(client => (
                      <Client key={client.id} client={client} />
                    ))}
                    <LoadingModule loading={loading} boxProps={{ border: 0 }} />
                  </Box>
                  {filteredClientsV4.length > 0 && (
                    <DashboardModule.LoadMoreFooter
                      callbacks={{ loadMore: () => loadMore() }}
                      canLoadMore={hasMore}
                      entityCount={filteredClientsV4?.length}
                    />
                  )}
                  {!loading && !filteredClientsV4.length && (
                    <DashboardModule.BlankState imageUrl={blankState}>
                      <DashboardModule.BlankState.Title>No Clients Available</DashboardModule.BlankState.Title>
                      <DashboardModule.BlankState.Paragraph>
                        Try adjusting your filters.
                      </DashboardModule.BlankState.Paragraph>
                    </DashboardModule.BlankState>
                  )}
                </Box>
              </DashboardModule>
            </Columns.Main>
            <Columns.Sidebar>
              <DashboardModule title="Tools">
                <Tools />
              </DashboardModule>
            </Columns.Sidebar>
          </Columns>
        </Box>
        <EditClientModal
          callbacks={callbacks('EditClientModal', setState)}
          showModal={showEditClientModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default ClientList
